import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useTransactions } from './useTransactions';
import { useGoals } from './useGoals';
import { 
  calculateMonthlySummary, 
  calculateFocoScore,
  MonthlySummary,
} from '@/lib/foco-calculations';

export function useMonthlySummary(year: number, month: number) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { transactions } = useTransactions(year, month);
  const { goals } = useGoals();

  // Calculate summary from transactions
  const calculatedSummary = (() => {
    if (!transactions.length) return null;

    const income = transactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + Number(t.amount), 0);
    
    const fixedExpenses = transactions
      .filter(t => t.type === 'fixed_expense')
      .reduce((sum, t) => sum + Number(t.amount), 0);
    
    const variableExpenses = transactions
      .filter(t => t.type === 'variable_expense')
      .reduce((sum, t) => sum + Number(t.amount), 0);
    
    const savings = transactions
      .filter(t => t.type === 'saving')
      .reduce((sum, t) => sum + Number(t.amount), 0);
    
    const investments = transactions
      .filter(t => t.type === 'investment')
      .reduce((sum, t) => sum + Number(t.amount), 0);
    
    const debtPayments = transactions
      .filter(t => t.type === 'debt_payment')
      .reduce((sum, t) => sum + Number(t.amount), 0);

    const summary = calculateMonthlySummary(
      income,
      fixedExpenses,
      variableExpenses,
      savings,
      investments,
      debtPayments
    );

    const activeGoals = goals
      .filter(g => g.status === 'active')
      .map(g => ({
        id: g.id,
        name: g.name,
        target_amount: Number(g.target_amount),
        current_amount: Number(g.current_amount),
        status: g.status as 'active' | 'completed' | 'paused',
      }));

    const focoScore = calculateFocoScore(summary, activeGoals);

    return { ...summary, foco_score: focoScore };
  })();

  // Fetch stored summary from database
  const { data: storedSummary, isLoading } = useQuery({
    queryKey: ['monthly-summary', user?.id, year, month],
    queryFn: async () => {
      if (!user) return null;
      
      const { data, error } = await supabase
        .from('monthly_summaries')
        .select('*')
        .eq('user_id', user.id)
        .eq('year', year)
        .eq('month', month)
        .maybeSingle();

      if (error) throw error;
      return data as MonthlySummary | null;
    },
    enabled: !!user,
  });

  // Save/update summary
  const saveSummary = useMutation({
    mutationFn: async (summary: MonthlySummary) => {
      if (!user) throw new Error('No user');

      const { data, error } = await supabase
        .from('monthly_summaries')
        .upsert({
          user_id: user.id,
          year,
          month,
          ...summary,
        }, {
          onConflict: 'user_id,year,month',
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['monthly-summary'] });
    },
  });

  const activeGoalsWithProgress = goals.filter(
    g => g.status === 'active' && Number(g.target_amount) > 0 && (Number(g.current_amount) / Number(g.target_amount)) >= 0.10
  ).length;

  return {
    summary: calculatedSummary,
    storedSummary,
    isLoading,
    saveSummary,
    hasActiveGoals: goals.filter(g => g.status === 'active').length > 0,
    activeGoalsWithProgress,
  };
}
